import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography } from '@mui/material';

import { ResponsivePie } from '@nivo/pie';
import { toHoursAndMinutes } from 'util/date.utils';
import { meryville } from 'util/themes';
import { ThemeContext } from 'util/themes/themeContext';

interface IPathwayPie {
    percentComplete: number;
    projectCompletionTime?: number;
    sx?: any;
}

interface IPathwayPieFilling {
    percentComplete: number;
    projectCompletionTime?: number;
    sx?: any;
}

const margin = { top: 0, right: 0, bottom: 0, left: 0 };

const styles = {
    root: {
        backgroundColor: 'unset',
        boxShadow: 'none',
        textAlign: 'left',
        position: 'relative',
        p: 1,
        height: {
            xs: 130,
            sm: 130,
            md: 200,
        },
    } as const,
    overlay: {
        p: 4,
        position: 'absolute',
        color: meryville.palette.primary.dark,
        top: 0,
        fontSize: '20px',
        fontWeight: 'bold',
        letterSpacing: 0,
        lineHeight: '30px',
        right: margin.right,
        bottom: 0,
        left: margin.left,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        // This is important to preserve the chart interactivity
        pointerEvents: 'none',
    },
    totalLabel: {
        fontSize: { xs: '14px', md: '20px' },
        fontWeight: 'bold',
        color: meryville.palette.text.primary
    },
};

function PathwayPieFilling(props: IPathwayPieFilling) {
    const { percentComplete, projectCompletionTime, sx } = props;
    const [innerText, setInnerText] = useState<any>({
        topText: '',
        bottomText: '',
    });

    useEffect(() => {
        if (percentComplete === 100) {
            setInnerText({ topText: 'Goal', bottomText: 'Achieved!' });
        } else if (percentComplete >= 75 && percentComplete <= 99) {
            setInnerText({ topText: 'Almost', bottomText: 'There!' });
        } else if (percentComplete >= 50 && percentComplete < 76) {
            setInnerText({ topText: 'Awesome', bottomText: 'Progress' });
        } else if (percentComplete >= 25 && percentComplete < 50) {
            setInnerText({ topText: 'Keep', bottomText: 'Going!' });
        } else if (percentComplete >= 1 && percentComplete < 25) {
            setInnerText({ topText: 'Great', bottomText: 'Start!' });
        } else {
            setInnerText({ topText: toHoursAndMinutes(projectCompletionTime) });
        }
    }, [percentComplete, projectCompletionTime]);

    return (
        <Box sx={styles.overlay}>
            <Typography sx={{ ...styles.totalLabel, ...sx }}>{innerText?.topText}</Typography>
            <Typography sx={{ ...styles.totalLabel, ...sx }}>
                {innerText?.bottomText}
            </Typography>
        </Box>
    );
}

export function PathwayPie(props: IPathwayPie) {
    const { percentComplete, projectCompletionTime, sx } = props;
    const { selectedTheme } = useContext(ThemeContext);

    const getData = () => {
        if (!percentComplete) {
            return [{ id: 'x', value: 100 }];
        } else {
            return [
                { id: 'y', value: 100 - percentComplete },
                { id: 'xAxis', value: percentComplete },
            ];
        }
    };
    return (
        <Box sx={styles.root}>
            <ResponsivePie
                startAngle={360}
                endAngle={-360}
                margin={margin}
                data={getData()}
                innerRadius={0.85}
                enableArcLabels={false}
                enableArcLinkLabels={false}
                isInteractive={false}
                defs={[
                    {
                        id: 'gray',
                        type: 'linearGradient',
                        colors: [
                            { offset: 0, color: meryville.palette.grey[100] },
                            { offset: 100, color: meryville.palette.grey[200] },
                        ],
                    },
                    {
                        id: 'blueGreen',
                        type: 'linearGradient',
                        gradientTransform: 'rotate(180 0.5 0.5)',
                        colors: [
                            { offset: 40, color: selectedTheme?.palette?.pieColor?.primary || '#C2F5E0', opacity: 100 },
                            { offset: 100, color: selectedTheme?.palette?.pieColor?.secondary || '#69D7D2', opacity: 100 },
                        ],
                    },
                ]}
                fill={[
                    { match: { id: 'xAxis' }, id: 'blueGreen' },
                    { match: '*', id: 'gray' },
                ]}
            />
            <PathwayPieFilling
                percentComplete={percentComplete}
                projectCompletionTime={projectCompletionTime}
                sx={sx}
            />
        </Box>
    );
}
